/**
 * CandidateRecommendations - Interactive drag-and-drop candidate ranking component for semantic search results
 * Features: Manual reordering via @dnd-kit, expandable cards with detailed candidate analysis
 * Dependencies: @dnd-kit for drag-drop, react-bootstrap for UI, supports i18n translations
 */
import React, { useState } from "react";
import {
  Card,
  Badge,
  Button,
  Collapse,
  Row,
  Col,
  Alert
} from "react-bootstrap";
import { useTranslation } from "react-i18next";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy
} from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import "./SemanticSearch.scss";

// Sortable Candidate Card Component
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const SortableCandidateCard = ({
  candidate,
  index,
  isExpanded,
  onToggleExpand,
  onViewCandidate,
  t
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging
  } = useSortable({
    id: candidate.candidateId || `candidate-${index}`,
    transition: {
      duration: 300, // milliseconds for smooth animations
      easing: "cubic-bezier(0.25, 1, 0.5, 1)" // smooth easing curve
    }
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition, // Use the transition from useSortable
    opacity: isSortableDragging ? 0.5 : 1,
    zIndex: isSortableDragging ? 1000 : "auto" // Bring dragged item to front
  };

  const getRankBadgeColor = (rank) => {
    const numRank = Number(rank);
    if (numRank === 1) return "primary";
    if (numRank <= 3) return "secondary";
    return "light";
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      className={`candidate-card ${isExpanded ? "expanded" : ""} ${
        isSortableDragging ? "is-dragging" : ""
      }`}
    >
      <Card.Header className="candidate-header" style={{ cursor: "pointer" }}>
        <Row className="align-items-center">
          {/* Drag handle - visible for manual reordering */}
          <Col xs="auto">
            {/* Drag Handle */}
            <div
              className="drag-handle"
              {...attributes}
              {...listeners}
              style={{
                cursor: "grab",
                padding: "8px",
                color: "#6c757d",
                fontSize: "1.2rem",
                opacity: 0.7
              }}
              title="Drag to reorder candidates"
            >
              <i className="fas fa-grip-vertical"></i>
            </div>
          </Col>
          <Col onClick={() => onToggleExpand()}>
            <div className="candidate-title">
              <Badge
                bg={getRankBadgeColor(candidate.rank)}
                className="rank-badge me-2"
                style={{
                  backgroundColor: (() => {
                    const rank = Number(candidate.rank);
                    if (rank === 1) return "#007bff";
                    if (rank <= 3) return "#6c757d";
                    return "#f8f9fa";
                  })(),
                  color: (() => {
                    const rank = Number(candidate.rank);
                    if (rank === 1) return "#ffffff";
                    if (rank <= 3) return "#ffffff";
                    return "#000000";
                  })(),
                  fontWeight: "700",
                  fontSize: "0.9rem",
                  border: "2px solid rgba(0,0,0,0.2)",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
                }}
              >
                #{candidate.rank}
              </Badge>
              <h5 className="candidate-name mb-0">{candidate.candidateName}</h5>
            </div>
          </Col>
          <Col xs="auto" onClick={() => onToggleExpand()}>
            <div className="candidate-score">
              <Badge
                className="score-badge"
                style={{
                  backgroundColor: "#007bff",
                  color: "#ffffff",
                  fontWeight: "700",
                  fontSize: "1rem",
                  minWidth: "70px",
                  textAlign: "center",
                  display: "block",
                  margin: "2px 0",
                  border: "2px solid rgba(0,0,0,0.2)",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
                }}
              >
                <i className="fas fa-star me-1"></i>
                {candidate.matchScore
                  ? `${Number(candidate.matchScore).toFixed(1)}`
                  : "N/A"}
              </Badge>
            </div>
          </Col>
          <Col xs="auto" onClick={() => onToggleExpand()}>
            <i
              className={`fas fa-chevron-${
                isExpanded ? "up" : "down"
              } expand-icon`}
            ></i>
          </Col>
        </Row>
      </Card.Header>

      <Collapse in={isExpanded}>
        <div>
          <Card.Body>
            {/* Justification */}
            <div className="candidate-section mb-3">
              <h6 className="section-title">
                <i className="fas fa-lightbulb me-2"></i>
                {t("Justification")}
              </h6>
              <p className="justification-text">{candidate.justification}</p>
            </div>

            {/* Key Strengths */}
            {candidate.keyStrengths && candidate.keyStrengths.length > 0 && (
              <div className="candidate-section mb-3">
                <h6 className="section-title">
                  <i className="fas fa-thumbs-up me-2 text-success"></i>
                  {t("Key Strengths")}
                </h6>
                <div className="strengths-list">
                  {candidate.keyStrengths.map((strength, idx) => (
                    <div
                      key={idx}
                      className="strength-item d-flex align-items-start mb-2"
                      style={{
                        backgroundColor: "#f8f9fa",
                        border: "1px solid #28a745",
                        borderRadius: "6px",
                        padding: "8px 12px",
                        fontSize: "0.875rem",
                        lineHeight: "1.4"
                      }}
                    >
                      <i
                        className="fas fa-check me-2 mt-1"
                        style={{
                          color: "#28a745",
                          fontSize: "0.75rem",
                          flexShrink: 0
                        }}
                      ></i>
                      <span style={{ color: "#495057" }}>{strength}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Potential Concerns */}
            {candidate.potentialConcerns &&
              candidate.potentialConcerns.length > 0 && (
                <div className="candidate-section mb-3">
                  <h6 className="section-title">
                    <i className="fas fa-exclamation-triangle me-2 text-warning"></i>
                    {t("Potential Concerns")}
                  </h6>
                  <div className="concerns-list">
                    {candidate.potentialConcerns.map((concern, idx) => (
                      <div key={idx} className="concern-item">
                        <i className="fas fa-minus-circle me-2 text-warning"></i>
                        <span>{concern}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

            {/* Interview Focus */}
            {candidate.recommendedInterviewFocus && (
              <div className="candidate-section mb-3">
                <h6 className="section-title">
                  <i className="fas fa-comments me-2 text-info"></i>
                  {t("Interview Focus")}
                </h6>
                <div className="interview-focus">
                  <i className="fas fa-quote-left me-2 text-dark"></i>
                  <span className="focus-text">
                    {candidate.recommendedInterviewFocus}
                  </span>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="candidate-actions">
              <Button
                variant="primary"
                size="sm"
                onClick={() => onViewCandidate(candidate.candidateId)}
                className="me-2"
              >
                <i className="fas fa-eye me-2"></i>
                {t("View")}
              </Button>
            </div>
          </Card.Body>
        </div>
      </Collapse>
    </Card>
  );
};

const CandidateRecommendations = ({ data, onViewCandidate }) => {
  const { t } = useTranslation();
  const [expandedCards, setExpandedCards] = useState(new Set([0])); // First card expanded by default
  const [candidates, setCandidates] = useState(data?.recommendations || []);
  const [activeId, setActiveId] = useState(null);

  // Update candidates when data changes
  React.useEffect(() => {
    if (data?.recommendations) {
      // Sort the recommendations by rank before setting them
      const sortedRecommendations = [...data.recommendations].sort((a, b) => {
        const rankA = Number(a.rank);
        const rankB = Number(b.rank);
        return rankA - rankB;
      });
      setCandidates(sortedRecommendations);
    }
  }, [data?.recommendations]);

  // Configure drag sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8
      }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setCandidates((items) => {
        const oldIndex = items.findIndex(
          (item) =>
            (item.candidateId || `candidate-${items.indexOf(item)}`) ===
            active.id
        );
        const newIndex = items.findIndex(
          (item) =>
            (item.candidateId || `candidate-${items.indexOf(item)}`) === over.id
        );

        return arrayMove(items, oldIndex, newIndex);
      });
    }

    setActiveId(null);
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  const toggleCard = (index) => {
    const newExpanded = new Set(expandedCards);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedCards(newExpanded);
  };

  const getRankBadgeColor = (rank) => {
    const numRank = Number(rank);
    if (numRank === 1) return "primary";
    if (numRank <= 3) return "secondary";
    return "light";
  };

  const formatProcessingTime = (timeString) => {
    if (!timeString) return "";

    // Parse time string like "00:00:21.9680137"
    const match = timeString.match(/(\d+):(\d+):(\d+)\.?(\d*)/);
    if (match) {
      const hours = parseInt(match[1]);
      const minutes = parseInt(match[2]);
      const seconds = parseInt(match[3]);

      if (hours > 0) {
        return `${hours}h ${minutes}m ${seconds}s`;
      } else if (minutes > 0) {
        return `${minutes}m ${seconds}s`;
      } else {
        return `${seconds}s`;
      }
    }
    return timeString;
  };

  if (!data || !data.recommendations || data.recommendations.length === 0) {
    return (
      <Alert variant="info">
        <i className="fas fa-info-circle me-2"></i>
        {t("No candidates found matching your criteria")}
      </Alert>
    );
  }

  return (
    <div className="candidate-recommendations">
      {/* Summary Card */}
      <Card className="summary-card mb-4">
        <Card.Body>
          <Row className="align-items-center">
            <Col md={6}>
              <div className="summary-stats">
                <div className="stat-item">
                  <div className="stat-number">{candidates.length}</div>
                  <div className="stat-label">{t("Candidates Found")}</div>
                </div>
                <div className="stat-item">
                  <div className="stat-number">
                    {formatProcessingTime(data.processingTime)}
                  </div>
                  <div className="stat-label">{t("Processing Time")}</div>
                </div>
              </div>
            </Col>
            <Col md={6}>
              <div className="summary-actions">
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => {
                    const allIndexes = new Set(
                      candidates.map((_, index) => index)
                    );
                    setExpandedCards(allIndexes);
                  }}
                  className="me-2"
                >
                  <i className="fas fa-expand-alt me-2"></i>
                  {t("Expand All")}
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => setExpandedCards(new Set())}
                >
                  <i className="fas fa-compress-alt me-2"></i>
                  {t("Collapse All")}
                </Button>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Overall Assessment */}
      {data.overallAssessment && (
        <Card className="assessment-card mb-4">
          <Card.Header>
            <h6 className="mb-0">
              <i className="fas fa-chart-pie me-2"></i>
              {t("Overall Assessment")}
            </h6>
          </Card.Header>
          <Card.Body>
            <p className="assessment-text">{data.overallAssessment}</p>
            {data.searchSummary && (
              <div className="search-summary">
                <Badge
                  bg="info"
                  className="me-2"
                  style={{
                    backgroundColor: "#17a2b8",
                    color: "#ffffff",
                    fontWeight: "500",
                    fontSize: "0.85rem"
                  }}
                >
                  <i
                    className="fas fa-clock me-1"
                    style={{ color: "#ffffff" }}
                  ></i>
                  {data.searchSummary}
                </Badge>
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Candidate Cards */}
      <div className="candidates-list">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
          modifiers={[restrictToVerticalAxis]}
        >
          <SortableContext
            items={candidates.map(
              (candidate) =>
                candidate.candidateId ||
                `candidate-${candidates.indexOf(candidate)}`
            )}
            strategy={verticalListSortingStrategy}
          >
            {candidates.map((candidate, index) => (
              <SortableCandidateCard
                key={candidate.candidateId || `candidate-${index}`}
                id={candidate.candidateId || `candidate-${index}`}
                candidate={candidate}
                index={index}
                isExpanded={expandedCards.has(index)}
                onToggleExpand={() => toggleCard(index)}
                onViewCandidate={onViewCandidate}
                t={t}
              />
            ))}
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );
};

export default CandidateRecommendations;
